import React, { useState } from 'react';
import { Box, VStack, Flex, Button, useColorModeValue } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, AddIcon } from '@chakra-ui/icons';
import { RoleCard } from './RoleCard';
import { Role } from '../../types';

export const DEPARTMENT_COLORS = [
  'blue.400',
  'green.400',
  'orange.400',
  'teal.400',
  'red.400',
  'purple.400',
  'cyan.400',
  'pink.400',
  'yellow.400'
];

interface RoleNodeProps {
  role: Role;
  allRoles: Role[];
  onRoleClick?: (role: Role) => void;
  onAddChildClick?: (parentRole: Role) => void;
  isSuperUser?: boolean;
  defaultExpanded?: boolean;
  inheritedColor?: string;
}

export const RoleNode: React.FC<RoleNodeProps> = ({
  role,
  allRoles,
  onRoleClick,
  onAddChildClick,
  isSuperUser,
  defaultExpanded = false,
  inheritedColor,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || role.level <= 2);
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  
  const children = allRoles
    .filter((r) => r.parentId === role.id)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const hasChildren = children.length > 0;
  const showChildrenArea = isExpanded && hasChildren;

  const renderItems = [...children];

  return (
    <VStack spacing={0} align="center" position="relative" w="max-content">
      <Box position="relative" zIndex={10} w="320px">
        <RoleCard
          role={role}
          onClick={isSuperUser ? () => onRoleClick?.(role) : undefined}
          onAddChild={isSuperUser ? () => onAddChildClick?.(role) : undefined}
          showAddChild={isSuperUser}
          isSuperUser={isSuperUser}
          isClickable={isSuperUser}
          themeColor={inheritedColor}
        />
        
        {/* Toggle Button if there are children */}
        {hasChildren && !isExpanded && (
          <Box position="absolute" bottom="-14px" left="50%" transform="translateX(-50%)" zIndex={20}>
            <Button
              size="xs"
              borderRadius="full"
              bg="white"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              onClick={() => setIsExpanded(true)}
              rightIcon={<ChevronDownIcon />}
              _hover={{ bg: 'gray.50' }}
              color="gray.600"
            >
              {children.length} {children.length === 1 ? 'role' : 'roles'} below
            </Button>
          </Box>
        )}
        {hasChildren && isExpanded && (
          <Box position="absolute" bottom="-14px" left="50%" transform="translateX(-50%)" zIndex={20}>
            <Button
              size="xs"
              borderRadius="full"
              bg="white"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              onClick={() => setIsExpanded(false)}
              rightIcon={<ChevronUpIcon />}
              _hover={{ bg: 'gray.50' }}
              color="gray.600"
            >
              Collapse
            </Button>
          </Box>
        )}
      </Box>

      {/* Expanded Children Area */}
      {showChildrenArea && (
        <Box w="full" position="relative" textAlign="center">
          {/* Vertical line from parent down to horizontal line */}
          <Box w="2px" h="8" bg={borderColor} mx="auto" position="relative" zIndex={0} />

          <Flex justify="center" position="relative">
            {renderItems.map((item, index, array) => {
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              const isOnly = array.length === 1;
              const isPlaceholder = typeof item === 'string';
              const childRole = isPlaceholder ? null : item as Role;

              let childColor = inheritedColor;
              if (childRole && childRole.level === 2) {
                childColor = DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length];
              }

              return (
                <Box key={isPlaceholder ? 'add-card' : item.id} position="relative" px={3} pb={4} minW="344px">
                  {/* Horizontal connecting line segment */}
                  {!isOnly && (
                    <Box
                      position="absolute"
                      top="0"
                      left={isFirst ? "50%" : "0"}
                      right={isLast ? "50%" : "0"}
                      h="2px"
                      bg={borderColor}
                      zIndex={0}
                    />
                  )}
                  {/* Vertical line down to card */}
                  <Box
                    position="absolute"
                    top="0"
                    left="50%"
                    transform="translateX(-50%)"
                    h="8"
                    w="2px"
                    bg={borderColor}
                    zIndex={0}
                  />
                  
                  {/* The actual Card or Placeholder */}
                  <Box mt={8} h="full">
                    {isPlaceholder ? (
                      <Box
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor="gray.300"
                        borderRadius="lg"
                        p={4}
                        h="120px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => onAddChildClick?.(role)}
                      >
                        <VStack spacing={2}>
                          <AddIcon color="gray.400" />
                          <Box color="gray.500" fontSize="sm">
                            Add under {role.title}
                          </Box>
                        </VStack>
                      </Box>
                    ) : (
                      <RoleNode
                        role={item as Role}
                        allRoles={allRoles}
                        onRoleClick={onRoleClick}
                        onAddChildClick={onAddChildClick}
                        isSuperUser={isSuperUser}
                        inheritedColor={childColor}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}
    </VStack>
  );
};
